/**
 * Rebuilds the destination RequireAuth bounced the visitor away from.
 *
 * RequireAuth saves the whole location object (`state={{ from: location }}`),
 * but every consumer used to read `state.from.pathname` and nothing else, so
 * the query string and hash were dropped on the way back. That is not cosmetic
 * here: the app deliberately encodes which conversation is open in the query
 * ("deep links arrive as ?threadId=/?userId=", see layout/chromeRoutes.js), so
 * a signed-out visitor opening /chat?threadId=<id> was returned to the thread
 * list with that conversation never opened — and, because chromeRoutes reads
 * the same query, without the immersive chrome the deep link asked for.
 *
 * Returns `fallback` untouched when there is no saved destination, which keeps
 * each caller's own landing page (LoginPage lands on the map, useHandleUsers
 * lands on the root) exactly as it was.
 */
export const intendedDestination = (location, fallback) => {
  const from = location?.state?.from;
  if (!from?.pathname) return fallback;
  return `${from.pathname}${from.search || ""}${from.hash || ""}`;
};

/**
 * Where the saved destination goes so it can survive an OAuth round trip.
 *
 * `state={{ from: location }}` lives in the router's in-memory history entry,
 * which is exactly what a full-page redirect to Google destroys — so the
 * "Continue with Google" button used to throw the destination away and land
 * everyone on the map while the typed-password button next to it honoured it
 * ([#77]). sessionStorage is the channel that survives both shapes of the
 * flow: the web redirect (same origin, same tab) and the native one (the app
 * stays alive behind a Custom Tab). It is already load-bearing in this exact
 * flow — `lib/authTrace.js` keeps the whole OAuth trace in sessionStorage
 * across this same round trip (`:13`, `:28`, `:167`) and its own comment at
 * `:142` says "same tab retains sessionStorage" — so this adds no new
 * dependency.
 *
 * An earlier draft of this comment cited Supabase's PKCE verifier as the
 * precedent instead. That was wrong and a skeptic caught it: GoTrueClient
 * takes `globalThis.localStorage` by default, and `probeAuthStorage` scans
 * LOCALSTORAGE for `sb-*-auth-token-code-verifier`. The conclusion survived
 * the correction; the evidence for it did not.
 */
export const INTENDED_DESTINATION_KEY = "tradeloft.intendedDestination";

/** Longest destination worth keeping. Real routes here are well under 200. */
const MAX_DESTINATION_LENGTH = 2048;

/**
 * Open-redirect guard.
 *
 * The value read back after the round trip has been outside this app's memory,
 * so it is untrusted input by the time it reaches `navigate()` even though we
 * are the ones who wrote it. Accept only a same-origin absolute path.
 */
export const isSafeInternalDestination = (value) => {
  if (typeof value !== "string") return false;
  if (value === "" || value.length > MAX_DESTINATION_LENGTH) return false;
  // Must be an absolute path on this origin.
  if (value[0] !== "/") return false;
  // "//evil.com" is protocol-relative and leaves the origin entirely. Browsers
  // also normalise a backslash into the authority position (WHATWG URL treats
  // "\" as "/" for special schemes), so reject the backslash outright rather
  // than trying to enumerate the shapes.
  //
  // Severity, stated honestly rather than dramatised: for THIS sink the
  // backslash form is defence in depth, not a live hole. The value reaches
  // react-router `navigate()` -> `history.pushState`, and pushState throws
  // SecurityError on a URL that resolves cross-origin instead of navigating
  // there. The guard earns its place because the sink could change; it is not
  // standing between today's users and an open redirect.
  if (value[1] === "/") return false;
  if (value.includes("\\")) return false;
  // A scheme can never appear in a path this app built for itself.
  if (value.includes("://")) return false;
  // Control characters cannot occur in a location react-router produced.
  // Written as a charCode scan rather than a regex class so the escape
  // sequences cannot be mangled by whatever writes this file.
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return false;
  }
  return true;
};

/**
 * Stash the destination before handing control to the OAuth provider.
 *
 * An unsafe or absent destination CLEARS the slot rather than leaving whatever
 * was there before: a stale entry from an abandoned sign-in must never steer a
 * later, unrelated one.
 */
export const saveIntendedDestination = (destination) => {
  try {
    if (isSafeInternalDestination(destination)) {
      window.sessionStorage.setItem(INTENDED_DESTINATION_KEY, destination);
    } else {
      window.sessionStorage.removeItem(INTENDED_DESTINATION_KEY);
    }
  } catch {
    // Storage disabled (private mode, locked-down WebView). Losing the deep
    // link degrades to the old behaviour — the caller's default landing page —
    // which is why every caller still passes its own fallback.
  }
};

/**
 * Read the destination back and consume it, returning null when there is
 * nothing safe to use.
 *
 * Consuming is the point of the name: the slot is cleared even when the value
 * fails the guard, so one abandoned attempt cannot redirect a later sign-in.
 */
export const takeIntendedDestination = () => {
  try {
    const stored = window.sessionStorage.getItem(INTENDED_DESTINATION_KEY);
    window.sessionStorage.removeItem(INTENDED_DESTINATION_KEY);
    return isSafeInternalDestination(stored) ? stored : null;
  } catch {
    return null;
  }
};

export default intendedDestination;
